/**
 * indexationSlice — l'état de préparation des documents pour l'assistant
 * (C19) : le lot en cours (un seul à la fois par fenêtre) et l'état connu de
 * chaque document, lu par la fenêtre du dossier, le lecteur du bureau et le
 * mobile. Les appels vivent dans `useIndexationLot` ; ici, l'état seul.
 */
import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import type { BulkJob, BulkJobItem, BulkJobStatus } from '../../services/bulkJobsAPI';
import type { IndexationInfo, IndexationStatus } from '../../utils/indexationLabels';
import type { RootState } from '../../app/store';

export interface LotEnCours {
  jobId: string;
  status: BulkJobStatus;
  progress: { applied: number; errors: number };
  resolvedCount: number;
  /** Phrase humaine de fin de lot (plafond du jour, coupe-circuit), telle que le serveur l'écrit. */
  message?: string;
  /** Les fichiers demandés, pour la fenêtre qui les montre. */
  fileIds: string[];
}

export interface IndexationState {
  lot: LotEnCours | null;
  /** État par fichier, tel que le lot ou la liste des fichiers l'ont dit en dernier. */
  etats: Record<string, IndexationInfo>;
  erreur: string | null;
}

const initialState: IndexationState = { lot: null, etats: {}, erreur: null };

const ETAT_PAR_ITEM: Partial<Record<BulkJobItem['status'], IndexationStatus>> = {
  pending: 'pending',
  processing: 'processing',
  applied: 'ready',
};

function lotDepuisJob(job: BulkJob, fileIds: string[]): LotEnCours {
  return {
    jobId: job._id,
    status: job.status,
    progress: { applied: job.progress?.applied ?? 0, errors: job.progress?.errors ?? 0 },
    resolvedCount: job.resolvedCount ?? fileIds.length,
    ...(job.message ? { message: job.message } : {}),
    fileIds,
  };
}

export const indexationSlice = createSlice({
  name: 'indexation',
  initialState,
  reducers: {
    lotDemarre(state, action: PayloadAction<{ job: BulkJob; fileIds: string[] }>) {
      state.lot = lotDepuisJob(action.payload.job, action.payload.fileIds);
      state.erreur = null;
      for (const fileId of action.payload.fileIds) {
        if (state.etats[fileId]?.status !== 'ready') state.etats[fileId] = { status: 'pending' };
      }
    },
    lotMisAJour(state, action: PayloadAction<{ job: BulkJob; items?: BulkJobItem[] }>) {
      const fileIds = state.lot?.jobId === action.payload.job._id ? state.lot.fileIds : [];
      state.lot = lotDepuisJob(action.payload.job, fileIds);
      for (const item of action.payload.items ?? []) {
        const status = ETAT_PAR_ITEM[item.status];
        if (status) state.etats[item.targetId] = { status };
        else if (item.status === 'error') state.etats[item.targetId] = { status: 'failed', ...(item.error ? { reason: item.error } : {}) };
      }
    },
    lotEnErreur(state, action: PayloadAction<string>) {
      state.erreur = action.payload;
    },
    lotOublie(state) {
      state.lot = null;
      state.erreur = null;
    },
    /** La liste des fichiers vient d'être relue : son état fait foi. */
    etatsDepuisFichiers(state, action: PayloadAction<Array<{ id: string; indexation?: IndexationInfo }>>) {
      for (const file of action.payload) {
        if (file.indexation) state.etats[file.id] = file.indexation;
        else delete state.etats[file.id];
      }
    },
  },
});

export const { lotDemarre, lotMisAJour, lotEnErreur, lotOublie, etatsDepuisFichiers } = indexationSlice.actions;

const VIDE: Record<string, IndexationInfo> = {};
// `state.indexation` peut manquer dans un store de test partiel : les sélecteurs rendent alors « rien ».
export const selectLotIndexation = (state: RootState): LotEnCours | null => state.indexation?.lot ?? null;
export const selectErreurIndexation = (state: RootState): string | null => state.indexation?.erreur ?? null;
export const selectEtatsIndexation = (state: RootState): Record<string, IndexationInfo> => state.indexation?.etats ?? VIDE;
export const selectEtatIndexation = (fileId: string | undefined) => (state: RootState): IndexationInfo | undefined =>
  (fileId ? state.indexation?.etats?.[fileId] : undefined);

/** L'état d'un document : ce que le lot a dit en dernier, sinon ce que la liste des fichiers porte. */
export function etatEffectif(etats: Record<string, IndexationInfo>, file: { id: string; indexation?: IndexationInfo } | null | undefined): IndexationInfo | undefined {
  if (!file) return undefined;
  return etats[file.id] ?? file.indexation;
}

export default indexationSlice.reducer;
